import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators"
import * as teamApi from "../../api/team"
import * as userApi from "../../api/user"
import * as urlUtil from "../../utils/url"

interface TeamUserInfo {
  openid: string
  nickname: string
  avatar: string
}

interface LoginState {
  openid: string
  openkey: string
  session_id: string
  session_type: string
  wx_appid?: string
  qq_appid?: string
}

interface PartitionInfo {
  game_platform_id: string
  area: string
  partition: string
}

interface Joiner {
  uin: string
  uin_type: string
  nick_img: string
  present_rank: string
  zone_id: string
}

type TeamDetailParams = {
  offerId: string
  loginState: LoginState
  partitionInfo: PartitionInfo
  drm_info: string
  team_id?: string
  sandbox?: string
  zone_id?: string
  action: string
  pagesize: number
  curpage: number
  sliceMember: number
}

interface StrangeTeamInfo {
  team_id: string
  avatar: string
  nickname: string
  team_members_number: string
}

interface FriendTeamInfo {
  team_id: string
  avatar: string
  nickname: string
  extend_info: string
}

function decodeNickNameAndAvatar(nick_img: string, decodeOnce = false): Pick<TeamUserInfo, "avatar" | "nickname"> {
  let result
  try {
    const nickAndAvatar = decodeOnce ? nick_img : decodeURIComponent(nick_img)
    const nickAndAvatarObj = urlUtil.getStrParams(nickAndAvatar, true)

    result = {
      avatar: nickAndAvatarObj?.img_url?.replace(/https?/gi, "https"),
      nickname: nickAndAvatarObj?.nick_name || nickAndAvatarObj?.nick_img
    }

    // 王者头像昵称是后台调王者接口获取的，多一层编码；其他游戏从前端传递的appmeta里取，少一层编码。
    // 统一按王者多decode一次，如果因为有特殊字符多decode一次会报错，则catch掉
    if (result.nickname) {
      try {
        result.nickname = decodeURIComponent(result.nickname)
      } catch (error) {
        console.log(error)
      }
    }
  } catch {
    result = {
      avatar: "",
      nickname: ""
    }
  }
  return result
}

@Module
export default class team extends VuexModule {
  userOpenid: string | null = null
  teamInfo: any = null
  captain: TeamUserInfo | null = null
  teamMembers: TeamUserInfo[] | null = null
  strangersTeam: StrangeTeamInfo[] | null = null
  friendsTeam: FriendTeamInfo[] | null = null
  participate: number | null = null
  get isInTeam(): boolean {
    return this.teamInfo?.join?.some((member: Joiner) => member.uin === this.userOpenid)
  }

  @Mutation
  setUserOpenid(openid: string): void {
    this.userOpenid = openid
  }
  @Mutation
  setTeamInfo(info: any): void {
    this.teamInfo = info
  }

  @Mutation
  setCaptainInfo(caption: TeamUserInfo): void {
    this.captain = caption
  }

  @Mutation
  setTeamMembersInfo(members: TeamUserInfo[]): void {
    this.teamMembers = members
  }

  @Mutation
  setStrangesTeamInfo(teams: StrangeTeamInfo[]): void {
    this.strangersTeam = teams
  }

  @Mutation
  setFriendsTeamInfo(teams: FriendTeamInfo[]): void {
    this.friendsTeam = teams
  }

  @Mutation
  setParticipate(userNums: number): void {
    this.participate = userNums
  }

  @Action({ rawError: true })
  resetTeam(): void {
    this.context.commit("setTeamInfo", null)
    this.context.commit("setCaptainInfo", null)
    this.context.commit("setTeamMembersInfo", null)
  }

  @Action({ rawError: true })
  async queryTeam({
    sandbox,
    offerId,
    loginState,
    drm_info,
    action,
    zone_id = "",
    partitionInfo,
    team_id = "",
    pagesize = 20,
    curpage = 1
  }: TeamDetailParams): Promise<any> {
    return await teamApi.teamProc(
      {
        sandbox,
        offerId
      },
      {
        ...loginState,
        drm_info,
        action,
        zone_id,
        ...partitionInfo,
        team_id,
        pagesize,
        curpage
      }
    )
  }

  @Action({ rawError: true })
  async getTeamDetail({
    offerId,
    loginState,
    partitionInfo,
    drm_info,
    team_id = "",
    sandbox,
    zone_id,
    pagesize,
    curpage,
    sliceMember
  }: TeamDetailParams): Promise<any> {
    const teamInfoRes = await this.context.dispatch("queryTeam", {
      sandbox,
      offerId,
      action: "query_team_detail",
      loginState,
      partitionInfo,
      drm_info,
      zone_id,
      team_id,
      pagesize,
      curpage
    })

    if (teamInfoRes?.info?.team_info?.team_id) {
      //队伍存在
      const info = teamInfoRes.info
      this.context.commit("setUserOpenid", info.user_openid)
      this.context.commit("setTeamInfo", info.team_info)

      const captain = info?.team_info?.join.find((j: Joiner) => {
        return j.uin == info?.team_info?.origin
      })
      this.context.commit("setCaptainInfo", captain)

      const members = info?.team_info?.join
        ?.map((joiner: Joiner) => {
          return {
            openid: joiner.uin,
            ...decodeNickNameAndAvatar(joiner.nick_img),
            zone_id: joiner.zone_id,
            present_rank: joiner.present_rank
          }
        })
        .sort(m => {
          if (m.openid == info.user_openid) {
            return -1
          }
        })
        .sort(m => {
          if (m.openid == info?.team_info?.origin) {
            return -1
          }
        })
        .slice(0, sliceMember ?? info?.team_info?.join?.length)
      //选出前5个，包含队长和自己,由于并发队伍里面可能大于5人,这里通过参数传过来

      this.context.commit("setUserOpenid", info.user_openid)
      this.context.commit("setTeamMembersInfo", members)

      return info
    } else {
      this.context.dispatch("resetTeam")
    }
  }

  @Action({ rawError: true, commit: "setStrangesTeamInfo" })
  async queryStrangeTeam({
    sandbox,
    offerId,
    loginState,
    drm_info
  }: Pick<TeamDetailParams, "sandbox" | "offerId" | "loginState" | "drm_info">): Promise<StrangeTeamInfo[]> {
    const res = await this.context.dispatch("queryTeam", {
      sandbox,
      offerId,
      action: "query_strange_team",
      loginState,
      drm_info
    })
    return res?.info?.strange_team_info?.map(team => {
      return {
        team_id: team.team_id,
        ...decodeNickNameAndAvatar(team.leader_nick_img),
        team_members_number: team.team_members_number
      }
    })
  }

  @Action({ rawError: true, commit: "setFriendsTeamInfo" })
  async queryFriendsTeam({
    sandbox,
    offerId,
    loginState,
    drm_info
  }: Pick<TeamDetailParams, "sandbox" | "offerId" | "loginState" | "drm_info">): Promise<FriendTeamInfo[]> {
    const res = await this.context.dispatch("queryTeam", {
      sandbox,
      offerId,
      action: "query_friends_team",
      loginState,
      drm_info
    })
    return res?.info?.friends_team_info?.map(team => {
      return {
        team_id: team.team_id,
        ...decodeNickNameAndAvatar(team.nick_img, true),
        extend_info: team.extend_info
      }
    })
  }

  @Action({ rawError: true })
  async queryTeamParticipant(params: any): Promise<any> {
    const res = await userApi.followActivity({
      offerId: params.offerId,
      sandbox: params.sandbox,
      ...params.loginState,
      drm_info: params.drm_info,
      accounttype: "qb",
      action: "drm_limit_query",
      buy_type: "buyteam",
      product_list: "JOIN",
      query_total: 1
    })

    const participate = res?.info?.limit_info?.resop_mpinfo?.detail?.[0]?.total_used_quota
    this.context.commit("setParticipate", participate)
    return participate
  }
}
